import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd"
import MDEditor from "@uiw/react-md-editor"
import React, { useCallback, useEffect, useRef, useState } from "react"

import { useDebounce } from "../hooks/useDebounce"
import { trpc } from "../lib/trpc"

interface TaskLabel {
  id: string | null
  name: string | null
  color: string | null
  description?: string | null
}

interface Subtask {
  id: string
  content: string
  completed: boolean
}

interface TaskEditorProps {
  task: {
    id: string
    content: string
    description?: string
    priority: number
    estimatedMinutes?: number
    status: "todo" | "in_progress" | "done"
    labels?: TaskLabel[]
    subtasks?: Subtask[]
  }
  onSave: (description: string) => void
  onUpdatePriority?: (priority: number) => void
  onUpdateEstimatedTime?: (minutes: number | null) => void
  onUpdateStatus?: (status: "todo" | "in_progress" | "done") => void
  onLabelsChange?: (labelIds: string[]) => void
  onClose: () => void
}

const TaskEditor: React.FC<TaskEditorProps> = ({
  task,
  onSave,
  onUpdatePriority,
  onUpdateEstimatedTime,
  onUpdateStatus,
  onLabelsChange,
  onClose,
}) => {
  const [localDescription, setLocalDescription] = useState(task.description || "")
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [showLabelsEditor, setShowLabelsEditor] = useState(false)
  const [optimisticLabels, setOptimisticLabels] = useState<TaskLabel[]>(task.labels || [])
  const labelsEditorRef = useRef<HTMLDivElement>(null)

  // Get available labels
  const { data: availableLabels } = trpc.brain.getTaskLabels.useQuery()

  // Labels mutations
  const assignLabelMutation = trpc.brain.assignTaskLabel.useMutation({
    onSuccess: (_, variables) => {
      // Find the label that was added
      if (variables && variables.labelId) {
        const addedLabel = availableLabels?.labels?.find((l) => l.id === variables.labelId)
        if (addedLabel) {
          setOptimisticLabels((prev) => [...prev, addedLabel])
        }
      }
    },
  })
  const removeLabelMutation = trpc.brain.removeTaskLabel.useMutation({
    onSuccess: (_, variables) => {
      if (variables && variables.labelId) {
        setOptimisticLabels((prev) => prev.filter((l) => l.id !== variables.labelId))
      }
    },
  })

  // Subtasks state and mutations
  const [subtasks, setSubtasks] = useState<Subtask[]>(task.subtasks || [])
  const [isGeneratingSubtasks, setIsGeneratingSubtasks] = useState(false)
  const [showSubtasks, setShowSubtasks] = useState(false)

  // Debounce subtasks updates
  const debouncedSubtasks = useDebounce(subtasks, 2000)

  // Generate subtasks mutation
  const generateSubtasksMutation = trpc.brain.generateSubtasks.useMutation({
    onSuccess: (data) => {
      setSubtasks(data.subtasks)
      setShowSubtasks(true)
      setIsGeneratingSubtasks(false)
    },
    onError: () => {
      setIsGeneratingSubtasks(false)
    },
  })

  // Update subtasks mutation
  const updateSubtasksMutation = trpc.brain.updateSubtasks.useMutation()

  // Debounce the description changes
  const debouncedDescription = useDebounce(localDescription, 3000)

  // Update local description when task changes
  useEffect(() => {
    setLocalDescription(task.description || "")
  }, [task.description])

  // Update optimistic labels when task labels change
  useEffect(() => {
    setOptimisticLabels(task.labels || [])
  }, [task.labels])

  // Update subtasks when task changes
  useEffect(() => {
    setSubtasks(task.subtasks || [])
    setShowSubtasks((task.subtasks || []).length > 0)
  }, [task.subtasks])

  // Save subtasks when debounced value changes
  useEffect(() => {
    if (debouncedSubtasks.length > 0 && JSON.stringify(debouncedSubtasks) !== JSON.stringify(task.subtasks || [])) {
      updateSubtasksMutation.mutate({
        taskId: task.id,
        subtasks: debouncedSubtasks,
      })
    }
  }, [debouncedSubtasks, task.subtasks, task.id, updateSubtasksMutation])

  // Close labels editor when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (labelsEditorRef.current && !labelsEditorRef.current.contains(event.target as Node)) {
        setShowLabelsEditor(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Save when debounced description changes
  useEffect(() => {
    if (debouncedDescription !== (task.description || "") && debouncedDescription !== undefined) {
      if (debouncedDescription.trim() !== (task.description || "").trim()) {
        setIsSaving(true)
        try {
          onSave(debouncedDescription)
          setLastSaved(new Date())
        } finally {
          setIsSaving(false)
        }
      }
    }
  }, [debouncedDescription, task.description, onSave])

  const handleDescriptionChange = useCallback((val?: string) => {
    setLocalDescription(val || "")
  }, [])

  const handlePriorityChange = (priority: number) => {
    if (onUpdatePriority) {
      onUpdatePriority(priority)
    }
  }

  const handleEstimatedTimeChange = (minutes: string) => {
    if (onUpdateEstimatedTime) {
      const numMinutes = minutes === "" ? null : parseInt(minutes)
      onUpdateEstimatedTime(numMinutes)
    }
  }

  const handleStatusChange = (status: "todo" | "in_progress" | "done") => {
    if (onUpdateStatus) {
      onUpdateStatus(status)
    }
  }

  const formatEstimatedTime = (minutes?: number) => {
    if (!minutes) return ""
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }

  const getPriorityColor = (priority: number) => {
    if (priority >= 4) return "text-destructive bg-red-50 border-red-200"
    if (priority >= 3) return "text-orange-600 bg-orange-50 border-orange-200"
    if (priority >= 2) return "text-yellow-600 bg-yellow-50 border-yellow-200"
    if (priority >= 1) return " bg-blue-50 border-blue-200"
    return " bg-foregroundx border-border"
  }

  const hasUnsavedChanges = localDescription !== (task.description || "")

  const handleToggleLabel = (labelId: string) => {
    const currentLabelIds = optimisticLabels.map((l) => l.id).filter((id): id is string => id !== null)
    const hasLabel = currentLabelIds.includes(labelId)

    if (hasLabel) {
      // Optimistically update
      setOptimisticLabels((prev) => prev.filter((l) => l.id !== labelId))
      removeLabelMutation.mutate({
        taskId: task.id,
        labelId,
      })
      // Update parent
      if (onLabelsChange) {
        const newLabelIds = currentLabelIds.filter((id) => id !== labelId)
        onLabelsChange(newLabelIds)
      }
    } else {
      // Find the label to add
      const labelToAdd = availableLabels?.labels?.find((l) => l.id === labelId)
      if (labelToAdd) {
        // Optimistically update
        setOptimisticLabels((prev) => [...prev, labelToAdd])
        assignLabelMutation.mutate({
          taskId: task.id,
          labelId,
        })
        // Update parent
        if (onLabelsChange) {
          const newLabelIds = [...currentLabelIds, labelId]
          onLabelsChange(newLabelIds)
        }
      }
    }
  }

  // Subtask handler functions
  const handleGenerateSubtasks = () => {
    setIsGeneratingSubtasks(true)
    generateSubtasksMutation.mutate({
      taskId: task.id,
    })
  }

  const handleSubtaskDragEnd = (result: any) => {
    if (!result.destination) return

    const items = Array.from(subtasks)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    setSubtasks(items)
  }

  const handleSubtaskChange = (subtaskId: string, field: "content" | "completed", value: string | boolean) => {
    setSubtasks((prev) => prev.map((subtask) => (subtask.id === subtaskId ? { ...subtask, [field]: value } : subtask)))
  }

  const handleAddSubtask = () => {
    const newSubtask: Subtask = {
      id: `subtask-${Date.now()}`,
      content: "",
      completed: false,
    }
    setSubtasks((prev) => [...prev, newSubtask])
  }

  const handleRemoveSubtask = (subtaskId: string) => {
    setSubtasks((prev) => prev.filter((subtask) => subtask.id !== subtaskId))
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header with task title and close button */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex-1 mr-4">
          <h2 className="text-lg font-semibold  line-clamp-2">{task.content}</h2>
        </div>
        <button onClick={onClose} className="text-gray-400 hover: transition-colors" title="Close editor">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Task Settings */}
      <div className="p-4 bg-foregroundx border-b border-border">
        <div className="flex flex-col gap-4">
          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Priority</label>
            <select
              value={task.priority}
              onChange={(e) => handlePriorityChange(parseInt(e.target.value))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${getPriorityColor(
                task.priority,
              )}`}
            >
              <option value={0}>0 - No Priority</option>
              <option value={1}>1 - Low</option>
              <option value={2}>2 - Medium</option>
              <option value={3}>3 - High</option>
              <option value={4}>4 - Urgent</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Status</label>
            <select
              value={task.status}
              onChange={(e) => handleStatusChange(e.target.value as "todo" | "in_progress" | "done")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="todo">Todo</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>

          {/* Estimated Time */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Estimated Time</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={task.estimatedMinutes || ""}
                onChange={(e) => handleEstimatedTimeChange(e.target.value)}
                placeholder="Minutes"
                min="0"
                step="15"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-sm text-gray-500 whitespace-nowrap">
                {task.estimatedMinutes ? formatEstimatedTime(task.estimatedMinutes) : ""}
              </span>
            </div>
          </div>

          {/* Labels */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Labels</label>
            <div className="relative">
              <button
                onClick={() => setShowLabelsEditor(!showLabelsEditor)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between"
              >
                <div className="flex items-center gap-1 flex-wrap">
                  {optimisticLabels.length > 0 ? (
                    optimisticLabels.map((label) => (
                      <span
                        key={label.id}
                        className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded"
                        style={{
                          backgroundColor: label.color ? `${label.color}20` : "#f3f4f620",
                          color: label.color || "#6b7280",
                        }}
                      >
                        {label.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">No labels</span>
                  )}
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Labels Editor Popover */}
              {showLabelsEditor && (
                <div
                  ref={labelsEditorRef}
                  className="absolute top-full left-0 mt-1 bg-background border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-full min-w-[250px]"
                >
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-primary mb-2">Select Labels</div>
                    {availableLabels && availableLabels.labels && availableLabels.labels.length > 0 ? (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {availableLabels.labels.map((label) => {
                          const isAssigned = optimisticLabels.some((l) => l.id === label.id)
                          return (
                            <label key={label.id} className="flex items-center gap-2 p-1  rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isAssigned}
                                onChange={() => handleToggleLabel(label.id)}
                                className="rounded border-gray-300  focus:ring-blue-500"
                              />
                              <span
                                className="inline-flex items-center px-2 py-1 text-xs rounded"
                                style={{
                                  backgroundColor: `${label.color}20`,
                                  color: label.color,
                                }}
                              >
                                {label.name}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">No labels available</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Description Editor */}
      <div className="flex-1 p-4 min-h-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-primary">Description</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {isSaving && (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                Saving...
              </span>
            )}
            {!isSaving && hasUnsavedChanges && (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                Unsaved changes
              </span>
            )}
            {!isSaving && !hasUnsavedChanges && lastSaved && (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        <div className="h-full min-h-[400px] border border-gray-300 rounded-lg overflow-hidden">
          <MDEditor
            value={localDescription}
            onChange={handleDescriptionChange}
            height="100%"
            preview="edit"
            hideToolbar={false}
            visibleDragbar={false}
            textareaProps={{
              placeholder:
                "Write task description here...\n\nYou can use Markdown formatting:\n- **bold text**\n- *italic text*\n- `code`\n- [links](url)\n- Lists, etc.",
              style: {
                fontSize: 14,
                lineHeight: 1.6,
                minHeight: "350px",
                resize: "none",
              },
              className: "min-h-[350px]",
            }}
            data-color-mode="light"
            style={{
              backgroundColor: "white",
            }}
          />
        </div>
      </div>

      {/* Subtasks Section */}
      <div className="border-t border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-primary">Subtasks</h3>
          <div className="flex items-center gap-2">
            {!showSubtasks && (
              <button
                onClick={handleGenerateSubtasks}
                disabled={isGeneratingSubtasks}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-blue-400 flex items-center gap-1"
              >
                {isGeneratingSubtasks ? (
                  <>
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </>
                ) : (
                  <>🤖 Break down into subtasks</>
                )}
              </button>
            )}
            {showSubtasks && (
              <div className="flex gap-2">
                <button
                  onClick={handleAddSubtask}
                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                >
                  + Add Subtask
                </button>
                <button
                  onClick={() => setShowSubtasks(false)}
                  className="px-2 py-1 bg-foregroundx0 text-white text-xs rounded hover:bg-gray-600"
                >
                  Hide
                </button>
              </div>
            )}
          </div>
        </div>

        {showSubtasks && (
          <DragDropContext onDragEnd={handleSubtaskDragEnd}>
            <Droppable droppableId="subtasks">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                  {subtasks.map((subtask, index) => (
                    <Draggable key={subtask.id} draggableId={subtask.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-3 p-3 bg-foregroundx rounded-lg border ${
                            snapshot.isDragging ? "shadow-lg bg-blue-50 border-blue-300" : "border-border"
                          }`}
                        >
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="flex flex-col gap-0.5 cursor-grab active:cursor-grabbing text-gray-400"
                          >
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                          </div>

                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={subtask.completed}
                            onChange={(e) => handleSubtaskChange(subtask.id, "completed", e.target.checked)}
                            className="rounded border-gray-300  focus:ring-blue-500"
                          />

                          {/* Content Input */}
                          <input
                            type="text"
                            value={subtask.content}
                            onChange={(e) => handleSubtaskChange(subtask.id, "content", e.target.value)}
                            placeholder="Enter subtask description..."
                            className={`flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              subtask.completed ? "line-through text-gray-500 bg-foregroundx" : ""
                            }`}
                          />

                          {/* Remove Button */}
                          <button
                            onClick={() => handleRemoveSubtask(subtask.id)}
                            className="text-destructive hover:text-destructive text-xs px-1"
                            title="Remove subtask"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {showSubtasks && subtasks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No subtasks yet.</p>
            <button onClick={handleAddSubtask} className="mt-2  hover:text-blue-700 text-sm">
              Add your first subtask
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default TaskEditor
